/*
 * MeetingAddenda — Iter 91 · Post-meeting voice-addendum surface.
 *
 * A meeting record can be enriched AFTER the primary transcript exists.
 * Common cases (per partner brief):
 *   · advisor started recording halfway through and needs to backfill
 *   · advisor remembers something important on the walk to the car
 *   · context that was never said aloud but matters for the record
 *
 * This component renders below the organized note body. It exposes:
 *   1. A collapsed "+ Add Voice Note" button.
 *   2. Expanded, a MeetingRecorder in transcript-only mode (audio is
 *      NOT retained — partner preference confirmed in Iter 90).
 *   3. The chronological list of prior addenda with delete controls.
 *   4. An "Update Organized Note" button that re-runs Organize with
 *      the transcript + all addenda merged (server-side, in
 *      `_organize_gather_material`).
 *
 * Provenance is preserved: transcripts land on `meeting_addenda[]`
 * (distinct from `meeting_transcript`) so the canonical meeting record
 * never gets silently overwritten with post-hoc recollection.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import MeetingRecorder from "@/components/MeetingRecorder";
import { Plus, Microphone, Trash, Sparkle } from "@phosphor-icons/react";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(iso).slice(0, 16);
  }
}

export default function MeetingAddenda({ note, onNoteChange }) {
  const [expanded, setExpanded] = useState(false);
  const [reorganizing, setReorganizing] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Only surface addenda affordances on notes that ALREADY have a
  // transcript. Adding a "voice note" to something that has never been
  // recorded is just... a recording. Users should start with the
  // primary MeetingRecorder in that case.
  const hasTranscript = !!(note.meeting_transcript && note.meeting_transcript.trim());
  const hasOrganized = !!note.meeting_structured;
  if (!hasTranscript && !hasOrganized) return null;

  const addenda = Array.isArray(note.meeting_addenda) ? note.meeting_addenda : [];

  const handleTranscribed = (data) => {
    if (!data?.addendum) return;
    onNoteChange?.({
      ...note,
      meeting_addenda: [...addenda, data.addendum],
    });
    setExpanded(false);
  };

  const removeAddendum = async (id) => {
    if (!id) return;
    setDeleting(id);
    try {
      await api.delete(`/notes/${note.id}/audio-addendum/${id}`);
      onNoteChange?.({
        ...note,
        meeting_addenda: addenda.filter((a) => a?.id !== id),
      });
      toast.success("Addendum removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const reorganize = async () => {
    setReorganizing(true);
    try {
      const { data } = await api.post(`/notes/${note.id}/organize`);
      onNoteChange?.({ ...note, ...data });
      toast.success("Organized note updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Organize failed");
    } finally {
      setReorganizing(false);
    }
  };

  return (
    <section data-testid="meeting-addenda">
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
        <Microphone size={12} weight="fill" /> Advisor addenda
        {addenda.length > 0 && <span>({addenda.length})</span>}
      </div>

      {addenda.length > 0 && (
        <ul className="space-y-2 mb-3">
          {addenda.map((a) => (
            <li
              key={a.id}
              data-testid={`meeting-addendum-${a.id}`}
              className="border-l-2 border-brass/60 pl-3 font-serif text-sm leading-relaxed flex items-start gap-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex flex-wrap items-center gap-3">
                  <span>{formatWhen(a.recorded_at)}</span>
                  {a.recorded_by_name && <span className="normal-case tracking-normal">by {a.recorded_by_name}</span>}
                  {a.duration_seconds ? <span>{Math.round(a.duration_seconds)}s</span> : null}
                </div>
                <div className="text-foreground/90 whitespace-pre-wrap">{a.transcript}</div>
              </div>
              <button
                type="button"
                onClick={() => removeAddendum(a.id)}
                disabled={deleting === a.id}
                aria-label="Remove addendum"
                title="Remove addendum"
                data-testid={`meeting-addendum-${a.id}-delete`}
                className="shrink-0 text-muted-foreground hover:text-destructive p-1 disabled:opacity-40"
              >
                <Trash size={14} weight="regular" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {expanded ? (
        <div data-testid="meeting-addenda-recorder">
          <MeetingRecorder
            noteId={note.id}
            onTranscribed={handleTranscribed}
            uploadPath="audio-addendum"
          />
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
            data-testid="meeting-addenda-cancel"
          >
            ← Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid="meeting-addenda-open"
            className="inline-flex items-center gap-1.5 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
          >
            <Plus size={11} weight="bold" /> Add voice note
          </button>
          {addenda.length > 0 && hasOrganized && (
            <button
              type="button"
              onClick={reorganize}
              disabled={reorganizing}
              data-testid="meeting-addenda-reorganize"
              className="inline-flex items-center gap-1.5 text-[0.6875rem] font-mono uppercase tracking-widest text-brass hover:opacity-80 disabled:opacity-40 transition-opacity"
              title="Re-run Organize with transcript + all addenda as source material. Existing partner refinements are preserved."
            >
              <Sparkle size={11} weight="fill" /> {reorganizing ? "Updating…" : "Update organized note"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
